/**
 * Redaktionelle Texte eines Artikels.
 *
 * Laedt die aufgeloesten Texte -- also einschliesslich der von der
 * Produktfamilie geerbten -- und uebergibt sie an die gemeinsame Darstellung.
 * Die Auflösung selbst liegt im Server (`core/texts.ts`); hier wird sie
 * nur uebersetzt in die Form, die `ProductTexts` erwartet.
 */
import { useCallback, useEffect, useState } from "react";
import type { ResolvedText, TextBasis } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { ProductTexts, type TextSlot } from "./ProductTexts.tsx";

export function ArticleTexts({ articleId, mayWrite, mayRelease, supplier, shortDescriptionRule }: {
  articleId: number;
  /** Die Oberflaeche richtet sich danach; entschieden wird im Server. */
  mayWrite: boolean;
  mayRelease: boolean;
  supplier: { id: number; name: string } | null;
  /**
   * Die regelgebildete Kurzbeschreibung, die der Produktkopf zeigt.
   *
   * Sie kommt aus derselben Serverantwort wie der Kopf selbst
   * (`ArticleDetail.shortDescriptionRule`) und wird hier nicht zweitgerechnet
   * -- zwei Rechenwege fuer eine Zeile waeren zwei Zeilen, sobald sich einer
   * von beiden aendert. Der Server bildet sie auf Deutsch; steht dort `null`,
   * hat die Regel keinen vollstaendigen Text ergeben und der Kopf zeigt auch
   * nichts.
   */
  shortDescriptionRule: string | null;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly ResolvedText[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setItems((await api.articleTexts(articleId)).items); }
    catch (e) { setError(e); }
  }, [articleId]);

  useEffect(() => { setItems(null); setEditing(null); void load(); }, [load]);

  if (error !== null && !items) {
    return <div className="texts"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!items) return <div className="texts"><LoadingState label={t("texts.loading")} /></div>;

  /*
   * Ein geerbter Text wird dort gepflegt, wo er herkommt -- er ist deshalb
   * hier nicht bearbeitbar. Er ist aber auch NICHT der Produkttext dieser
   * SKU: `ownAllowed` haelt den Weg zu einem eigenen offen.
   *
   * DER WIDERSPRUCH ZWISCHEN KOPF UND TEXTE-TAB.
   *
   * Der Produktkopf zeigt eine Kurzbeschreibung, die aus der Auswahlmatrix
   * der Produktfamilie gebildet und NICHT gespeichert wird. Der Texte-Tab
   * fand dazu nichts im Bestand und meldete „keine Fassung vorhanden" --
   * beides fuer sich richtig, zusammen eine Ansage, dass derselbe Text
   * existiert und nicht existiert. Aufgeloest wird das, indem der Tab die
   * Zeile NENNT und dazusagt, woher sie kommt.
   *
   * Nur an der Kurzbeschreibung: nur sie wird an anderer Stelle der Akte
   * tatsaechlich angezeigt. Fuer die uebrigen Textarten liesse sich zwar
   * ebenfalls ein Entwurf bilden, aber HOMEBASE verwendet ihn nirgends -- ihn
   * hier als geltenden Text auszugeben waere eine Behauptung, keine Auskunft.
   */
  /*
   * DER ARTIKELNAME STEHT NICHT IM TEXTE-TAB.
   *
   * Er wurde hier als eigene Textkarte gefuehrt -- oberhalb der Tabs steht
   * dieselbe SKU aber bereits mit Nummer und Bezeichnung, und eine zweite
   * Pflegestelle fuer denselben Namen war eine Stelle mehr, an der beide
   * auseinanderlaufen konnten. Entfernt ist NUR die Karte: die Textart
   * bleibt im Bestand, im Server und fuer jede andere Verwendung erhalten.
   */
  const slots: TextSlot[] = items.filter((i) => i.type !== "artikelname").map((i) => ({
    type: i.type, maxLength: i.maxLength, text: i.text,
    inherited: i.inherited, inheritedFrom: i.inheritedFrom, editable: !i.inherited,
    ownAllowed: i.ownAllowed,
    ruleText: i.type === "kurzbeschreibung" && shortDescriptionRule !== null
      ? { body: shortDescriptionRule, language: "de" as const } : null,
  }));
  const anyText = slots.some((s) => s.text !== null);

  return (
    <div className="texts">
      <h3 className="detail__section">{t("texts.section")}</h3>
      {!anyText && !mayWrite && <p className="texts__empty">{t("texts.emptyAll")}</p>}
      <ProductTexts
        slots={slots} subject={{ kind: "article", id: articleId }}
        mayWrite={mayWrite} mayRelease={mayRelease} supplier={supplier}
        editing={editing} setEditing={setEditing}
        onChanged={() => { setEditing(null); void load(); }}
        onError={setError}
      />
      <TextBasisPanel articleId={articleId} />
      {error !== null && <p className="texts__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * DIE TEXTGRUNDLAGE.
 *
 * Was ueber genau diese Ausfuehrung feststeht -- Einordnung, Familie,
 * Modell, die konkrete Ausfuehrung, die Merkmale dieser SKU.
 *
 * Der Nutzen ist heute schon da und hat mit KI nichts zu tun: man sieht auf
 * einen Blick, OB eine SKU vollstaendig genug beschrieben ist, um einen
 * Produkttext zu tragen. Wer hier nur eine Artikelnummer sieht, braucht
 * keinen Texter, sondern Daten.
 *
 * Es wird HIER NICHTS ERZEUGT. Genau diese Zusammenstellung ist die
 * Schnittstelle, an der eine Texterzeugung spaeter andockt.
 */
function TextBasisPanel({ articleId }: { articleId: number }): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [basis, setBasis] = useState<{ basis: TextBasis; fingerprint: string } | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => { setBasis(null); setOffen(false); }, [articleId]);

  async function oeffnen(): Promise<void> {
    setOffen(true);
    if (basis) return;
    try { setBasis(await api.textBasis(articleId)); }
    catch (e) { setFehler(e); }
  }

  if (!offen) {
    return (
      <div className="texts__basis">
        <button className="btn btn--small" onClick={() => void oeffnen()}>{t("texts.basisShow")}</button>
      </div>
    );
  }

  const zeile = (k: string, v: string) => (
    <div className="texts__basisrow" key={k}>
      <span className="texts__basiskey">{k}</span>
      <span className="texts__basisval">{v}</span>
    </div>
  );

  return (
    <div className="texts__basis">
      <h4 className="texts__basisgroup">{t("texts.basisTitle")}</h4>
      <p className="texts__basisnote">{t("texts.basisNote")}</p>
      {fehler !== null && <p className="texts__error" role="alert">{errorText(i18n, fehler)}</p>}
      {basis === null ? <LoadingState label={t("texts.loading")} /> : (
        <>
          {zeile(t("texts.basisNumber"), basis.basis.articleNumber)}
          {zeile(t("texts.basisCategory"),
            basis.basis.category ? i18n.tCode("category", basis.basis.category, basis.basis.category) : "—")}
          {zeile(t("texts.basisFamily"), basis.basis.family?.name ?? "—")}
          {zeile(t("texts.basisModel"), basis.basis.model?.name ?? "—")}

          <h4 className="texts__basisgroup">{t("texts.basisExecution")}</h4>
          {basis.basis.execution.length === 0
            ? <p className="texts__basisnote">{t("texts.basisEmptyExecution")}</p>
            : basis.basis.execution.map((e) => zeile(
                i18n.tCode("prop", e.dimension, e.dimension),
                e.unit === "°" ? `${e.value}${e.unit}` : e.unit ? `${e.value} ${e.unit}` : e.value))}

          <h4 className="texts__basisgroup">{t("texts.basisProperties")}</h4>
          {basis.basis.properties.length === 0
            ? <p className="texts__basisnote">{t("texts.basisEmptyProperties")}</p>
            : basis.basis.properties.map((m, idx) => (
                <div className="texts__basisrow" key={`${m.code}:${idx}`}>
                  <span className="texts__basiskey">{i18n.tCode("prop", m.code, m.code)}</span>
                  <span className="texts__basisval">
                    {m.unit === "°" ? `${m.value}${m.unit}` : m.unit ? `${m.value} ${m.unit}` : m.value}
                  </span>
                </div>
              ))}

          {zeile(t("texts.basisFingerprint"), basis.fingerprint)}
          <p className="texts__basisnote">{t("texts.basisGenerateLater")}</p>
        </>
      )}
      <button className="btn btn--small" onClick={() => setOffen(false)}>{t("texts.basisHide")}</button>
    </div>
  );
}
